import { captureException } from '@sentry/react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSetAtom } from 'jotai';
import {
  getArticlePriceData,
  getInventoryArticleType,
  nextQuantity,
  useParameters,
} from '@web/utils/src';
import plusIconDark from '@web/utils/src/assets/icons/functions/plus-icon-dark.svg';
import trashIcon from '@web/utils/src/assets/icons/functions/trash-icon.svg';
import { useCustomToast } from '@/hooks/useCustomToast';
import { isCartOpenAtom, newCartItemAtom } from '@/store/atoms';
import { addToCartAtom, removeFromCartAtom } from '@/store/store';
import { Article } from '@/types';
import { AddToCartToast } from '../AddToCartToast';
import { ActionType } from '../AddToCartToast/AddToCartToast';

interface ControlCartItemProps {
  article: Article;
  quantityInCart: number;
}
interface CartParams {
  retailerId: string;
}

export type ItemType = 'singular' | 'countable' | 'loose';

const ControlCartItem = (props: ControlCartItemProps) => {
  const { retailerId } = useParameters<CartParams>();
  const { article, quantityInCart } = props;
  const setCartItems = useSetAtom(addToCartAtom);
  const newCartItem = useSetAtom(newCartItemAtom);
  const removeCartItem = useSetAtom(removeFromCartAtom);
  const setCartOpen = useSetAtom(isCartOpenAtom);
  const customToast = useCustomToast();
  const { t } = useTranslation();

  const itemType = getInventoryArticleType(article);

  const callToast = (type: ActionType) => {
    return toast(
      <AddToCartToast
        article={article}
        onClick={() => setCartOpen(true)}
        type={type}
      />,
      { duration: 3000, position: 'bottom-right' },
    );
  };

  const handleAddToCart = () => {
    let quantity = quantityInCart + 1;
    if (itemType === 'loose' && article.amount) {
      const deliveryOptions = article.quantityOptions?.delivery;
      if (!deliveryOptions || !deliveryOptions.length) {
        captureException(
          new Error(`quantity options not defined on article #${article.id}`),
        );
      }
      const incrementBy = nextQuantity(
        deliveryOptions as string[],
        quantityInCart,
      );
      quantity = incrementBy as number;
    }
    const { finalPrice } = getArticlePriceData(article);

    try {
      newCartItem(article);
      setCartItems({
        quantity,
        retailerID: retailerId,
        price: finalPrice,
      });
      callToast(ActionType.Increase);
    } catch (error: any) {
      customToast('error', error.message || t('global.something_went_wrong'));
      console.log(error);
    }
  };

  const handleRemoveFromCart = () => {
    let quantity = quantityInCart - 1;
    if (itemType === 'loose' && article.amount) {
      const deliveryOptions = article.quantityOptions?.delivery;
      if (!deliveryOptions || !deliveryOptions.length) {
        captureException(
          new Error(`quantity options not defined on article #${article.id}`),
        );
      }
      const decrementBy = nextQuantity(
        deliveryOptions as string[],
        quantityInCart,
        'decrement',
      );
      quantity = decrementBy as number;
    }

    removeCartItem({
      retailerID: retailerId,
      quantity: quantity,
      itemType: itemType,
      itemId: article.id,
    });
    callToast(quantity !== 0 ? ActionType.Decrease : ActionType.Removed);
  };

  const displayQuantity = () => {
    if (itemType === 'singular') return quantityInCart;
    const quantity = article.amount || 0;
    return `${quantity * quantityInCart} ${article.unit}`;
  };

  return (
    <div className="flex min-w-[22rem] md:min-w-[12.5rem] items-center rounded-base border-primary-green border overflow-hidden">
      <div
        className="w-1/4 p-1-0 md:p-0-5 cursor-pointer"
        onClick={handleRemoveFromCart}
      >
        <img src={trashIcon} />
      </div>
      <div className="w-2/4">
        <p className="text-center text-2-2 md:text-1-2 font-bd">
          {displayQuantity()}
        </p>
      </div>
      <div
        className="w-1/4 h-full bg-primary-green px-2-0 py-1-0 md:py-0 md:px-1-0 hover:bg-opacity-70 cursor-pointer"
        onClick={handleAddToCart}
      >
        <img src={plusIconDark} className="my-1-2" />
      </div>
    </div>
  );
};

export default ControlCartItem;
